/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { toolsetsRemoveOAuthServer } from "../funcs/toolsetsRemoveOAuthServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Toolset } from "../models/components/toolset.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RemoveOAuthServerRequest,
  RemoveOAuthServerSecurity,
} from "../models/operations/removeoauthserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoveOAuthServerMutationVariables = {
  request: RemoveOAuthServerRequest;
  security?: RemoveOAuthServerSecurity | undefined;
  options?: RequestOptions;
};

export type RemoveOAuthServerMutationData = Toolset;

export type RemoveOAuthServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * removeOAuthServer toolsets
 *
 * @remarks
 * Remove OAuth server association from a toolset
 */
export function useRemoveOAuthServerMutation(
  options?: MutationHookOptions<
    RemoveOAuthServerMutationData,
    RemoveOAuthServerMutationError,
    RemoveOAuthServerMutationVariables
  >,
): UseMutationResult<
  RemoveOAuthServerMutationData,
  RemoveOAuthServerMutationError,
  RemoveOAuthServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoveOAuthServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoveOAuthServer(): MutationKey {
  return ["@gram/client", "toolsets", "removeOAuthServer"];
}

export function buildRemoveOAuthServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoveOAuthServerMutationVariables,
  ) => Promise<RemoveOAuthServerMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoveOAuthServer(),
    mutationFn: function removeOAuthServerMutationFn({
      request,
      security,
      options,
    }): Promise<RemoveOAuthServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(toolsetsRemoveOAuthServer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
